<template>
  <Wrapper class="app app--auth">
    <div class="app-content">
      <div class="block block-shadow py-30 block-rounded">
        <header class="block-header text-center" v-if="isDefaultLogo != ''">
          <img :src="isDefaultLogo" alt="brand logo" />
        </header>
        <div>
          <div class="block-content p-0">
            <h1 class="block-title text-center">
              Sign in using your email
            </h1>
          </div>
          <div v-if="errors.length" class="block-content">
            <div class="alert alert-danger">
              <div v-for="(item, index) in errors" :key="'auth-error-' + index">
                {{ item.message }}
              </div>
            </div>
          </div>
          <div class="block-content">
            <form class="form">
              <div class="form-group">
                <div
                  class="form-material pt-0"
                  :class="email.error ? 'form-material--has-error' : ''"
                >
                  <input
                    type="email"
                    v-model="email.value"
                    name="email"
                    placeholder="Email"
                    class="form-control no-focus"
                    autofocus
                  />
                  <label for="email" class="sr-only">Email</label>
                </div>
                <label class="error-message" v-if="email.error">
                  Please enter a valid email address
                </label>
              </div>
              <div class="form-group">
                <div
                  class="form-material pt-0"
                  :class="password.error ? 'form-material--has-error' : null"
                >
                  <input
                    type="password"
                    v-model="password.value"
                    name="password"
                    id="password"
                    class="form-control no-focus"
                    placeholder="Password"
                  />
                  <label for="password" class="sr-only">Password</label>
                </div>
                <template v-if="password.error">
                  <label class="error-message" v-if="!error">
                    Sorry, that password isn't right. We can help you
                    <router-link to="/forgot">
                      recover your password.
                    </router-link>
                  </label>
                  <label class="error-message" v-else>
                    {{ error }}
                  </label>
                </template>
              </div>
              <div class="form-group row justify-content-between mb-30">
                <div class="col-auto">
                  <router-link
                    to="/forgot"
                    class="form-option form-option--muted"
                  >
                    Forgot password?
                  </router-link>
                </div>
                <div class="col-auto">
                  <router-link tag="a" to="/register" class="form-option">
                    Register
                  </router-link>
                </div>
              </div>
              <div class="form-group">
                <button
                  type="submit"
                  class="btn btn-gd btn-gd-primary btn-block"
                  style="color: #fff"
                  :disabled="isLoading"
                  @click="userLogin"
                >
                  <Spinner v-if="isLoading" theme="currentColor" />
                  <span v-else>Sign in</span>
                </button>
              </div>
            </form>
          </div>
          <template
            v-if="isMainSocialLogin.length && isMainSocialLogin[0].value.status"
          >
            <div class="block-content">
              <div class="sep sep--or">
                <span>OR</span>
              </div>
            </div>
            <div class="block-content">
              <h2 class="block-title text-center my-10">
                Sign in using social
              </h2>
            </div>
            <SocialAuth class="block-content text-center" />
          </template>
        </div>
      </div>
    </div>
  </Wrapper>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import authTypes from "@/store/auth/types";
import { filter } from "lodash";
import { socketSender } from "@/helpers/socket";
import axios from "@/helpers/axios";

const Wrapper = () => import("./components/Wrapper");
const Spinner = () => import("@/components/Spinner");
const SocialAuth = () => import("./components/SocialAuth");

export default {
  components: {
    Wrapper,
    SocialAuth,
    Spinner
  },
  data() {
    return {
      error: null,
      errors: [],
      email: {
        value: null,
        error: false
      },
      password: {
        value: null,
        error: false
      },
      authErrors: this.$store.state.auth.errors,
      isMainSocialLogin: false,
      isDefaultLogo: "",
      headroomLogo: require("@/assets/media/brand.svg"),
      isLoading: false
    };
  },
  mounted() {
    if (this.$route.params.errors) {
      this.errors = this.$route.params.errors;
    }

    if (this.isLoggedIn()) {
      this.$router.push("/user");
    }

    if (this.authErrors.length) {
      this.email.error = true;
      this.password.error = true;
      this.error = "Please enter your password";
    }
  },
  methods: {
    ...mapActions({
      AUTH_SET: "AUTH_SET",
      AUTH_CHECK: "AUTH_CHECK"
    }),
    ...mapGetters({
      isLoggedIn: "isLoggedIn",
      getUser: "getUser"
    }),
    async handleValidation({ email, password }) {
      this.error = null;
      this.email.error = false;
      this.password.error = false;
      await axios
        .get(`auth/verify-email?email=${email}`)
        .then(() => {
          return true;
        })
        .catch(() => {
          this.email.error = true;
          return false;
        });
      if (!password) {
        this.password.error = true;
        this.error = "Please enter your password";
        return false;
      }
      return true;
    },
    userLogin(event) {
      event.preventDefault();

      const user = {
        email: this.email.value,
        password: this.password.value
      };

      this.error = null;
      if (this.handleValidation(user)) {
        this.isLoading = true;

        this.AUTH_SET(user)
          .then(response => {
            this.$store.dispatch(authTypes.AUTH_SET_ERRORS, []).then(() => {
              if (!response) {
                return;
              }

              const roles = response.Roles.map(item => item.value);
              this.email.value = null;
              this.password.value = null;
              let dashboard = {};

              if (roles.includes(8)) {
                dashboard.name = "Admin Dashboard";
                dashboard.route = "/admin/dashboard";
              } else if (roles.includes(2)) {
                dashboard.name = "Concierge Dashboard";
                dashboard.route = "/concierge/dashboard";
              } else {
                dashboard.name = "User Dashboard";
                dashboard.route = "/user/dashboard";
              }

              /**
               * SOCKET: User Login to QSpace
               */
              let logContent = `${response.firstName} ${response.lastName} signed in on the QSpace ${dashboard.name}`;
              socketSender("userLogin", logContent).then(() => {
                /**
                 * Redirect to their assigned dashboard
                 */
                window.location.href = dashboard.route;
              });
            });
          })
          .catch(() => {
            this.password.error = true;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    }
  },
  computed: {
    options() {
      return this.$store.state.concierge.optionsList.data;
    },
    scheme() {
      return this.$store.state.concierge.schemeList.data;
    }
  },
  async created() {
    await Promise.all([this.$store.dispatch("fetchOptions")]);

    this.isMainSocialLogin = filter(this.options, ["key", "sociallogin.main"]);
    let defaultLogoStatus = filter(this.options, ["key", "defaultlogo.main"]);
    if (defaultLogoStatus.length && !defaultLogoStatus[0].value.status) {
      let defaultLogo = filter(this.options, ["key", "defaultlogo.sub"]);
      if (defaultLogo.length && defaultLogo[0].value.image) {
        this.isDefaultLogo = defaultLogo[0].value.image;
      } else {
        this.isDefaultLogo = this.headroomLogo;
      }
    } else {
      this.isDefaultLogo = this.headroomLogo;
    }
  }
};
</script>
<style lang="scss" scoped>
.block-header {
  overflow: hidden;
}
.form-group {
  .error-message {
    color: $brand-danger;
  }
  a {
    color: var(--primary, $brand-primary);
  }
}
</style>
